import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, RefreshCw, AlertCircle, ExternalLink, ReceiptText } from "lucide-react";
import { fmtMoney } from "@/lib/intl";

function fmtDate(epoch) {
  if (!epoch) return "—";
  const d = new Date(epoch * 1000);
  return d.toLocaleDateString("en-AU", { year: "numeric", month: "short", day: "2-digit" });
}

function StatusBadge({ status }) {
  const map = {
    succeeded: "bg-emerald-50 border-emerald-200 text-emerald-800",
    failed: "bg-red-50 border-red-200 text-red-800",
    pending: "bg-amber-50 border-amber-200 text-amber-800",
  };
  const cls = map[status] || "bg-zinc-50 border-zinc-200 text-zinc-700";
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs rounded border ${cls}`}>
      {status}
    </span>
  );
}

export default function StripePayments({ email }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  // Map: chargeId -> { sale_id, generated_at } for receipts already created.
  const [receiptedByCharge, setReceiptedByCharge] = useState({});

  async function load() {
    if (!email) return;
    setLoading(true);
    setErr(null);
    setNotConfigured(false);
    try {
      const r = await api.get("/stripe/payments/by-email", { params: { email }, timeout: 60000 });
      setData(r.data);
      // After loading charges, query the receipts log in ONE bulk call so we
      // can render an "Already receipted" badge against each succeeded charge.
      const succeededIds = (r.data.charges || [])
        .filter((c) => c.status === "succeeded")
        .map((c) => c.id)
        .slice(0, 500);
      if (succeededIds.length > 0) {
        try {
          const bulk = await api.post("/receipts/by-source/bulk", {
            source: "membership",
            source_ids: succeededIds,
          }, { timeout: 20000 });
          setReceiptedByCharge(bulk.data || {});
        } catch (_) {
          setReceiptedByCharge({});
        }
      } else {
        setReceiptedByCharge({});
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 400 && /not configured/i.test(detail)) {
        setNotConfigured(true);
      } else {
        setErr(detail);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setData(null);
    setErr(null);
    setNotConfigured(false);
    if (email) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  if (!email) {
    return (
      <div className="text-sm text-zinc-500 border-2 border-dashed border-zinc-200 rounded-md p-6 text-center bg-zinc-50">
        This order has no email — Stripe lookup needs a customer email.
      </div>
    );
  }

  if (notConfigured) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-900">
        <div className="font-semibold">Stripe is not connected.</div>
        <div className="mt-1">
          Go to <b>Settings</b> → <b>Stripe — Payment reconciliation</b> and paste your restricted key
          to enable full subscription payment history.
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-900 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">Stripe lookup failed</div>
          <div className="font-mono-num text-xs mt-1">{err}</div>
          <Button size="sm" variant="outline" className="mt-2 h-8" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        Querying Stripe for <span className="font-mono-num">{email}</span>…
      </div>
    );
  }

  if (!data) return null;

  const charges = data.charges || [];
  const summary = data.summary || {};
  const customers = data.customers || [];

  if (customers.length === 0) {
    return (
      <div className="text-sm text-zinc-700 border-2 border-dashed border-zinc-200 rounded-md p-6 text-center bg-zinc-50">
        No Stripe customer found for <span className="font-mono-num">{email}</span>.
        <br />
        <span className="text-xs text-zinc-500">
          Either this customer pays outside Stripe or their Stripe email differs from their Acuity email.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="stripe-payments">
      {/* Summary card */}
      <div className="border border-zinc-200 rounded-md bg-zinc-50 p-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-zinc-500">Stripe customers</div>
            <div className="font-mono-num font-semibold">{customers.length}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500">Total charges</div>
            <div className="font-mono-num font-semibold">{summary.count || 0}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500">Succeeded</div>
            <div className="font-mono-num font-semibold text-emerald-700">
              {summary.succeeded_count || 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-zinc-500">Total paid</div>
            <div className="font-mono-num font-semibold">
              {fmtMoney((summary.total_paid_cents || 0) / 100)}
              {summary.currencies?.length === 1 && summary.currencies[0] !== "aud" && (
                <span className="ml-1 text-xs uppercase text-zinc-500">
                  {summary.currencies[0]}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="mt-3 h-8 border-zinc-300"
          onClick={load}
          data-testid="stripe-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Customer chips */}
      {customers.length > 1 && (
        <div className="text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded p-2">
          ⚠ {customers.length} Stripe customer records share this email. Charges from all of them are
          combined below.
        </div>
      )}

      {/* Charges table */}
      {charges.length === 0 ? (
        <div className="text-sm text-zinc-500 border-2 border-dashed border-zinc-200 rounded-md p-6 text-center bg-zinc-50">
          No charges found for this customer in Stripe.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-xs uppercase tracking-wider text-zinc-600">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2 w-44">Actions</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((ch) => (
                <tr
                  key={ch.id}
                  className={`border-t border-zinc-100 ${ch.status !== "succeeded" ? "bg-red-50/30" : ""}`}
                >
                  <td className="px-3 py-2 font-mono-num text-zinc-900">{fmtDate(ch.created)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">
                    {fmtMoney(ch.amount / 100)}
                    {ch.amount_refunded > 0 && (
                      <div className="text-2xs text-red-600 font-mono-num">
                        − {fmtMoney(ch.amount_refunded / 100)} refunded
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={ch.status} />
                    {ch.failure_message && (
                      <div className="text-2xs text-red-700 mt-1">{ch.failure_message}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-700 max-w-xs">
                    <div className="truncate" title={ch.description || ""}>
                      {ch.description || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      {ch.status === "succeeded" && email && (
                        receiptedByCharge[ch.id] ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/receipts?regen=${encodeURIComponent(receiptedByCharge[ch.id].sale_id)}`)}
                            className="inline-flex items-center gap-1.5 h-7 px-2 text-2xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 font-mono-num"
                            data-testid={`already-receipted-${ch.id}`}
                            title={`Sale ${receiptedByCharge[ch.id].sale_id} · ${receiptedByCharge[ch.id].generated_at?.slice(0, 10)}${receiptedByCharge[ch.id].emailed_to ? ` · emailed to ${receiptedByCharge[ch.id].emailed_to}` : ""}`}
                          >
                            <ReceiptText className="h-3.5 w-3.5 text-emerald-700" />
                            {receiptedByCharge[ch.id].sale_id}
                          </button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 px-2"
                            onClick={() => {
                              const qs = new URLSearchParams({
                                source: "membership",
                                email,
                                charge_id: ch.id,
                              }).toString();
                              navigate(`/receipts?${qs}`);
                            }}
                            data-testid={`generate-membership-receipt-${ch.id}`}
                            title="Generate claim receipt for this renewal payment"
                          >
                            <ReceiptText className="h-3.5 w-3.5 mr-1" />
                            Receipt
                          </Button>
                        )
                      )}
                      {ch.receipt_url && (
                        <a
                          href={ch.receipt_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-zinc-500 hover:text-zinc-950 px-1"
                          title="View Stripe receipt"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-2xs text-zinc-500 flex items-center gap-1.5">
        <CreditCard className="h-3 w-3" /> Source: Stripe (read-only). Acuity's API only exposes the
        original subscription order — all renewal charges live in Stripe.
      </div>
    </div>
  );
}
